import React, { useState, useRef, useCallback, useEffect } from 'react';
import './PhotoModal.css';
import ApproveRejectButtons from '../ApproveRejectButtons';
import ImageTools from '../ImageTools/ImageTools';
import { INSPECTION_STATUS_VERDICT } from '../../utils/constants';
import UpdatePhotoModal from '../UpdatePhotoModal';

const { PENDING, APPROVED, REJECTED } = INSPECTION_STATUS_VERDICT;

const PhotoModal = ({ selectedPhoto, closeModal, open, handleApprovePhoto, handleRejectPhoto, enableApproveButtons, enableRejectButtons, updatePhoto, enableUpdatePhotoButton, onDownloadPhoto }) => {
    const [currentImageUrl, setCurrentImageUrl] = useState('');
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const imageRef = useRef(null);
    const viewportRef = useRef(null);
    const transformLayerRef = useRef(null);
    const videoRef = useRef(null);

    const [photoRotation, setPhotoRotation] = useState(0);
    const isVideo = selectedPhoto?.isVideo || selectedPhoto?.id === 'video360';

    const getPhotoUrl = (photo) => {
        return photo.url;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleRotationChange = useCallback((newRotation) => {
        if (!isVideo) {
            setPhotoRotation(newRotation);
            if (selectedPhoto?.id) {
                localStorage.setItem(`photo-rotation-${selectedPhoto.id}`, newRotation.toString());
            }
        }
    }, [selectedPhoto?.id, isVideo]);

    const handlePhotoUpdate = async (oldPhoto, newPhotoData) => {
        updatePhoto(oldPhoto, newPhotoData);
        setShowUpdateModal(false);
    };

    const handleDownloadPhoto = () => {
        if (onDownloadPhoto && selectedPhoto) {
            onDownloadPhoto(selectedPhoto);
        }
    };

    useEffect(() => {
        if (open && selectedPhoto) {
            setCurrentImageUrl('');
            if (!isVideo && selectedPhoto?.id) {
                const savedRotation = localStorage.getItem(`photo-rotation-${selectedPhoto.id}`);
                setPhotoRotation(savedRotation ? parseInt(savedRotation, 10) : 0);
            }
        }
    }, [open, selectedPhoto, isVideo]);

    if (!open) return null;

    const getPhotoState = () => {
        const status = selectedPhoto?.status;
        if (status === APPROVED || status === 'aprobado') return APPROVED;
        if (status === REJECTED || status === 'rechazado') return REJECTED;
        return PENDING;
    };

    return (
        <div className="photo-modal" onClick={closeModal}>
            <div className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="photo-modal-toolbar">
                    <div className="photo-modal-status-badge">
                        <span className={`status-badge ${getPhotoState()}`}>
                            {getPhotoState() === APPROVED ? 'Aprobado' :
                                getPhotoState() === REJECTED ? 'Rechazado' :
                                    'Pendiente'}
                        </span>
                    </div>
                    <div className="photo-modal-header">
                        <button
                            className="photo-modal-download"
                            onClick={handleDownloadPhoto}
                            title={isVideo ? 'Descargar video' : 'Descargar foto'}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7,10 12,15 17,10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                        </button>
                        <button className="photo-modal-close" onClick={closeModal} aria-label="Cerrar">
                            ×
                        </button>
                    </div>
                </div>

                <div className="photo-modal-body">
                    <div className="photo-modal-photo-container">
                        {isVideo ? (
                            <video
                                ref={videoRef}
                                src={getPhotoUrl(selectedPhoto, 'high')}
                                controls
                                className="photo-modal-video"
                                controlsList="nodownload"
                            >
                                Tu navegador no soporta la reproducción de videos.
                            </video>
                        ) : (
                            <>
                                <div className="photo-modal-viewport" ref={viewportRef}>
                                    <div className="photo-modal-transform-layer" ref={transformLayerRef}>
                                        <img
                                            ref={imageRef}
                                            src={currentImageUrl || getPhotoUrl(selectedPhoto, 'high')}
                                            alt="Foto ampliada"
                                            className="photo-modal-photo"
                                            draggable={false}
                                            onError={(e) => {
                                                e.target.src = '/placeholder-image.png';
                                                e.target.alt = 'Error al cargar imagen';
                                            }}
                                        />
                                    </div>
                                </div>
                                <ImageTools
                                    imageRef={imageRef}
                                    viewportRef={viewportRef}
                                    transformLayerRef={transformLayerRef}
                                    initialRotation={photoRotation}
                                    onRotationChange={handleRotationChange}
                                    photoId={selectedPhoto?.id}
                                />
                            </>
                        )}
                    </div>
                </div>

                <div className="photo-modal-footer">
                    <div className="photo-modal-info">
                        <div className="photo-modal-info-title">
                            <h3>{selectedPhoto.name || selectedPhoto.label}</h3>
                        </div>
                        <div className="photo-modal-info-details">
                            <p>Fecha de subida: {formatDate(selectedPhoto.uploadedAt)}</p>
                            {selectedPhoto.location && <p>Ubicación: {selectedPhoto.location}</p>}
                            {isVideo && selectedPhoto.duration && (
                                <p>Duración: {selectedPhoto.duration} segundos</p>
                            )}
                            {selectedPhoto.finishedBy && (
                                <>
                                    <p>{selectedPhoto.status === APPROVED ? 'Aprobado por:' : 'Rechazado por:'} {selectedPhoto.finishedBy}</p>
                                    <p>{selectedPhoto.status === APPROVED ? 'Fecha de aprobación:' : 'Fecha de rechazo:'} {formatDate(selectedPhoto.finishedAt)}</p>
                                    {selectedPhoto.rejectionReason && <p><strong>Motivo de rechazo:</strong> {selectedPhoto.rejectionReason}</p>}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="photo-modal-actions">
                        <ApproveRejectButtons
                            enableApproveButtons={enableApproveButtons()}
                            enableRejectButtons={enableRejectButtons()}
                            handleApproveClick={() => handleApprovePhoto(selectedPhoto)}
                            handleRejectClick={() => handleRejectPhoto(selectedPhoto)}
                            areRequiredFieldsComplete={() => true}
                        />

                        {enableUpdatePhotoButton() && !isVideo && (
                            <div className="photo-modal-update-photo-button">
                                <button className="btn btn-primary" onClick={() => setShowUpdateModal(true)}>
                                    Actualizar Foto
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {!isVideo && (
                    <UpdatePhotoModal
                        isOpen={showUpdateModal}
                        onClose={() => setShowUpdateModal(false)}
                        photo={selectedPhoto}
                        onPhotoUpdate={handlePhotoUpdate}
                    />
                )}
            </div>
        </div>
    );
};

export default PhotoModal;
